import React from 'react';
import { ViberMessageOptions } from '../types/viber.types';
import Icon from './Icon';
import { useT } from '../i18n/LanguageContext';

interface ViberMessagePreviewProps {
  viberContent: ViberMessageOptions;
  recipientData: {
    name: string;
    phoneNumber: string;
    customData: Record<string, any>;
  };
}

const ViberMessagePreview: React.FC<ViberMessagePreviewProps> = ({ viberContent, recipientData }) => {
  const t = useT();
  // Replace placeholders in text
  const replacePlaceholders = (text: string): string => {
    return text
      .replace(/\{\{name\}\}/g, recipientData.name)
      .replace(/\{\{phone\}\}/g, recipientData.phoneNumber)
      .replace(/\{\{custom\.(\w+)\}\}/g, (match, fieldName) => {
        return recipientData.customData[fieldName] || match;
      });
  };

  const personalizedText = viberContent.text ? replacePlaceholders(viberContent.text) : '';
  const personalizedButtonAction = viberContent.button?.action
    ? replacePlaceholders(viberContent.button.action)
    : '';

  const messageType = (() => {
    if (viberContent.carousel) return 'carousel';
    if (viberContent.video) return 'video';
    if (viberContent.image && viberContent.text) return 'image-text';
    if (viberContent.image) return 'image';
    if (viberContent.button) return 'text-button';
    return 'text';
  })();

  return (
    <div style={{
      backgroundColor: '#f5f5f5',
      padding: '20px',
      borderRadius: '10px',
      maxWidth: '400px',
      margin: '0 auto',
    }}>
      {/* Phone Frame */}
      <div style={{
        backgroundColor: '#7c3aed',
        borderRadius: '30px',
        padding: '20px 15px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      }}>
        {/* Status Bar */}
        <div style={{
          backgroundColor: '#6c2bd9',
          borderRadius: '20px 20px 0 0',
          padding: '10px 15px',
          display: 'flex',
          justifyContent: 'space-between',
          color: 'white',
          fontSize: '12px',
          marginBottom: '5px',
        }}>
          <span>9:41</span>
          <span></span>
        </div>

        {/* Message Container */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0 0 20px 20px',
          padding: '20px',
          minHeight: '400px',
          maxHeight: '500px',
          overflowY: 'auto',
        }}>
          {/* Sender Name */}
          <div style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#7c3aed',
            marginBottom: '10px',
          }}>
            {t('Vaša firma', 'YourBusiness')}
          </div>

          {/* Message Badge */}
          <div style={{
            display: 'inline-block',
            padding: '3px 8px',
            backgroundColor: viberContent.label === 'TRANSACTION' ? '#17a2b8' : '#7c3aed',
            color: 'white',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: 'bold',
            marginBottom: '10px',
          }}>
            {viberContent.label || 'PROMOTION'}
          </div>

          {/* Empty State */}
          {!personalizedText && !viberContent.image && !viberContent.video && !viberContent.carousel && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#999',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}><Icon name="viber" size={40} /></div>
              <div>{t('Pregled vaše Viber poruke će se prikazati ovde', 'Your Viber message preview will appear here')}</div>
            </div>
          )}

          {/* Text Message */}
          {personalizedText && (
            <div style={{
              backgroundColor: '#f0f0f0',
              padding: '12px 15px',
              borderRadius: '18px',
              marginBottom: '10px',
              fontSize: '14px',
              lineHeight: '1.4',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {personalizedText}
            </div>
          )}

          {/* Image */}
          {viberContent.image && (
            <div style={{ marginBottom: '10px' }}>
              <img
                src={viberContent.image}
                alt="Message"
                style={{
                  width: '100%',
                  borderRadius: '12px',
                  maxHeight: '250px',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.insertAdjacentHTML('afterend', `<div style="padding: 20px; background: #fee; color: #c00; border-radius: 12px; text-align: center;">${t('Slika nije učitana', 'Image failed to load')}</div>`);
                }}
              />
            </div>
          )}

          {/* Video */}
          {viberContent.video && (
            <div style={{
              position: 'relative',
              marginBottom: '10px',
              borderRadius: '12px',
              overflow: 'hidden',
            }}>
              <img
                src={viberContent.video.thumbnail}
                alt="Video thumbnail"
                style={{
                  width: '100%',
                  maxHeight: '250px',
                  objectFit: 'cover',
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60px',
                height: '60px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '30px',
              }}>
                <Icon name="play" size={26} />
              </div>
              <div style={{
                position: 'absolute',
                bottom: '10px',
                right: '10px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
              }}>
                {Math.floor(viberContent.video.duration / 60)}:{String(viberContent.video.duration % 60).padStart(2, '0')}
              </div>
            </div>
          )}

          {/* Carousel */}
          {viberContent.carousel && (
            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '10px',
              padding: '5px 0',
            }}>
              {viberContent.carousel.items.map((item, index) => (
                <div
                  key={index}
                  style={{
                    minWidth: '200px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    style={{
                      width: '100%',
                      height: '120px',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="120"%3E%3Crect fill="%23ddd" width="200" height="120"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <div style={{ padding: '12px' }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '13px',
                      marginBottom: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {replacePlaceholders(item.title)}
                    </div>
                    <button
                      type="button"
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#7c3aed',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      {item.primaryButton.text}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Button */}
          {viberContent.button && personalizedText && (
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {viberContent.button.caption}
              </button>
              <div style={{
                marginTop: '5px',
                fontSize: '11px',
                color: '#666',
                textAlign: 'center',
                wordBreak: 'break-all',
              }}>
                <Icon name="link" size={12} /> {personalizedButtonAction}
              </div>
            </div>
          )}

          {/* Message Info */}
          <div style={{
            marginTop: '15px',
            paddingTop: '10px',
            borderTop: '1px solid #eee',
            fontSize: '11px',
            color: '#999',
            textAlign: 'center',
          }}>
            {t('Tip poruke:', 'Message Type:')} <strong>{messageType}</strong>
            {viberContent.tag && (
              <div>{t('Tag:', 'Tag:')} {viberContent.tag}</div>
            )}
            <div>TTL: {Math.floor((viberContent.ttl || 86400) / 3600)}h</div>
          </div>
        </div>
      </div>

      {/* Character Count & Warnings */}
      <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
        {personalizedText && (
          <div style={{
            padding: '8px',
            backgroundColor: personalizedText.length > 800 ? '#fff3cd' : '#d4edda',
            borderRadius: '5px',
            marginBottom: '8px',
          }}>
            {t('Tekst:', 'Text:')} {personalizedText.length} / 1000 {t('znakova', 'characters')}
            {personalizedText.length > 800 && t(' Razmislite o skraćivanju', ' Consider shortening')}
          </div>
        )}

        {viberContent.label === 'TRANSACTION' && (viberContent.button || viberContent.image || viberContent.video) && (
          <div style={{
            padding: '8px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '5px',
          }}>
            <Icon name="x" size={14} /> {t('TRANSACTION poruke ne mogu imati dugmad, slike ili video!', 'TRANSACTION messages cannot have buttons, images, or videos!')}
          </div>
        )}

        {viberContent.button && !personalizedText && (
          <div style={{
            padding: '8px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '5px',
          }}>
            <Icon name="x" size={14} /> {t('Dugmad zahtevaju tekstualnu poruku!', 'Buttons require text message!')}
          </div>
        )}

        {viberContent.carousel && viberContent.carousel.items.length < 2 && (
          <div style={{
            padding: '8px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '5px',
          }}>
            <Icon name="x" size={14} /> {t('Carousel mora imati najmanje 2 kartice!', 'Carousel must have at least 2 items!')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViberMessagePreview;
